import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    IReclassificationAccountRef,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';

/**
 * Nested overlay used to create a manual account on-the-fly from within the
 * reclassification modal. Emits the new account's composite ref + selected FLI
 * id when the user confirms; the parent inserts it into the live mapping.
 */
@Component({
    selector: 'aumico-new-account-nested-modal',
    templateUrl: './new-account-nested-modal.component.html',
    styleUrls: ['./new-account-nested-modal.component.scss'],
})
export class NewAccountNestedModalComponent implements OnInit {
    /** Statement determines which FLI options are shown. */
    @Input() statement!: ReclassificationStatement;

    /**
     * Account numbers that already exist in the active mapping (regular accounts,
     * grouped accounts, sub-FLI accounts, and synthetic phantom reclass
     * accounts). The new account number must not collide with any of these.
     */
    @Input() existingAccountNumbers: Set<string> = new Set();

    /**
     * FLIs of the current statement (BS or ER). The user must pick the FLI
     * under which the new phantom reclass account will live.
     */
    @Input() fliOptions: Array<{ id: string; label: string }> = [];

    @Output() created = new EventEmitter<{
        ref: IReclassificationAccountRef;
        fliId: string;
        statement: ReclassificationStatement;
    }>();
    @Output() cancelled = new EventEmitter<void>();

    accountNumber = '';
    accountName = '';
    fliId = '';
    submitAttempted = false;

    // Localized texts
    title = '';
    accountNumberLabel = '';
    accountNumberPlaceholder = '';
    accountNameLabel = '';
    accountNamePlaceholder = '';
    fliLabel = '';
    fliPlaceholder = '';
    cancelLabel = '';
    saveLabel = '';
    errAccountNumberRequired = '';
    errAccountNumberDuplicate = '';
    errAccountNameRequired = '';
    errFliRequired = '';

    constructor(private readonly i18n: I18NextService) {}

    ngOnInit(): void {
        const t = (k: string): string => this.i18n.t(k) as string;
        this.title = t('aumico_grouping_masking_reclass_new_account_modal_title');
        this.accountNumberLabel = t('aumico_grouping_masking_reclass_new_account_number_label');
        this.accountNumberPlaceholder = t('aumico_grouping_masking_reclass_new_account_number_placeholder');
        this.accountNameLabel = t('aumico_grouping_masking_reclass_new_account_name_label');
        this.accountNamePlaceholder = t('aumico_grouping_masking_reclass_new_account_name_placeholder');
        this.fliLabel = t('aumico_grouping_masking_reclass_new_account_fli_label');
        this.fliPlaceholder = t('aumico_grouping_masking_reclass_new_account_fli_placeholder');
        this.cancelLabel = t('aumico_grouping_masking_modal_cancel');
        this.saveLabel = t('aumico_grouping_masking_reclass_new_account_save');
        this.errAccountNumberRequired = t('aumico_grouping_masking_reclass_new_account_number_required');
        this.errAccountNumberDuplicate = t('aumico_grouping_masking_reclass_new_account_number_duplicate');
        this.errAccountNameRequired = t('aumico_grouping_masking_reclass_new_account_name_required');
        this.errFliRequired = t('aumico_grouping_masking_reclass_new_account_fli_required');
    }

    get isAccountNumberEmpty(): boolean {
        return this.accountNumber.trim().length === 0;
    }

    /**
     * True when the entered account number already exists somewhere in the
     * statement. Lookup uses the account number alone — collisions on number
     * are rejected regardless of name, because the saved AR's mapping treats
     * the number as the primary identifier for downstream resolution.
     */
    get isAccountNumberDuplicate(): boolean {
        const trimmed = this.accountNumber.trim();
        if (trimmed.length === 0) return false;
        return this.existingAccountNumbers.has(trimmed);
    }

    get isAccountNameEmpty(): boolean {
        return this.accountName.trim().length === 0;
    }

    get isFliEmpty(): boolean {
        return this.fliId.trim().length === 0;
    }

    get hasErrors(): boolean {
        return this.isAccountNumberEmpty || this.isAccountNumberDuplicate || this.isAccountNameEmpty || this.isFliEmpty;
    }

    onSave(): void {
        this.submitAttempted = true;
        if (this.hasErrors) return;
        this.created.emit({
            ref: { accountNumber: this.accountNumber.trim(), name: this.accountName.trim() },
            fliId: this.fliId,
            statement: this.statement,
        });
    }

    onCancel(): void {
        this.cancelled.emit();
    }
}
